import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useStickyFilter } from '../hooks/useStickyFilter';
import {
  FileText,
  Calendar,
  Clock,
  ExternalLink,
  Search,
  RefreshCw,
  User,
  ChevronRight,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { useCachedData } from '../hooks/useCachedData';

// Portal Modal Component - Renders outside of dashboard container
const TaskModal = ({ task, onClose }) => {
  // Handle escape key and body scroll
  useEffect(() => {
    // Prevent body scroll
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '15px'; // Prevent layout shift
    
    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    
    // Cleanup
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Helper functions for task data
  const getFMSType = (task) => task?.fms || 'Unknown FMS';
  const getTaskInfo = (task) => task?.task_info || '';
  const getWhatToDo = (task) => task?.what_to_do || '';
  const getDoer = (task) => task?.doer || '';
  const getPlannedDate = (task) => task?.planned || '';
  const getDelay = (task) => task?.delay || '';
  const getPCDeo = (task) => task?.pcdeo || '';
  const getDoerEmail = (task) => task?.doer_email || '';
  const getWeek = (task) => task?.week || '';
  const getLink = (task) => task?.link || '';

  // Helper function to clean task info text
  const getCleanTaskInfo = (task) => {
    const taskInfo = getTaskInfo(task);
    if (!taskInfo) return '';
    
    return taskInfo
      .replace(/<br\s*\/?>/gi, ' ') // Remove <br> tags
      .replace(/\n/g, ' ') // Remove line breaks
      .replace(/\r/g, ' ') // Remove carriage returns
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim(); // Remove leading/trailing whitespace
  };

  // Helper functions to extract data from task info
  const parseTaskInfo = (taskInfo) => {
    if (!taskInfo) return {};
    
    const info = {};
    const lines = taskInfo.split('<br>');
    
    lines.forEach(line => {
      if (line.includes('Oder ID') || line.includes('Order ID')) {
        info.orderId = line.split(' - ')[1] || '';
      } else if (line.includes('Name of the Client')) {
        info.clientName = line.split(' - ')[1] || '';
      } else if (line.includes('Mobile')) {
        info.mobile = line.split(' - ')[1] || '';
      } else if (line.includes('Invoice Amount')) {
        info.invoiceAmount = line.split(' - ')[1] || '';
      } else if (line.includes('Invoice No')) {
        info.invoiceNo = line.split(' - ')[1] || '';
      } else if (line.includes('Order Taken By')) {
        info.orderTakenBy = line.split(' - ')[1] || '';
      }
    });
    
    return info;
  };

  const taskInfo = parseTaskInfo(getTaskInfo(task));

  // Create modal content
  const modalContent = (
    <div className="k-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="k-modal" style={{ maxWidth: '60rem' }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="k-modal-header">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-widest opacity-70 mb-1">FMS Task</p>
            <h3 className="text-lg font-bold leading-snug">{getFMSType(task)}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {taskInfo.orderId && <span className="k-badge-delegated">Order: {taskInfo.orderId}</span>}
              {getDelay(task) && parseFloat(getDelay(task)) > 0 && <span className="k-badge-overdue">Delayed</span>}
            </div>
          </div>
          <button className="k-modal-close flex-shrink-0" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="k-modal-body">

          {/* Info Grid */}
          <div className="k-info-grid">
            {/* FMS Information */}
            <div>
              <p className="k-section-title">FMS Information</p>
              <div className="flex flex-col gap-2.5">
                {[
                  { label: 'FMS Type', value: getFMSType(task) },
                  { label: 'Order ID', value: taskInfo.orderId },
                  { label: 'Client Name', value: taskInfo.clientName },
                  { label: 'Mobile', value: taskInfo.mobile },
                  { label: 'Invoice No', value: taskInfo.invoiceNo },
                ].map((f, i) => (
                  <div key={i} className="k-info-block">
                    <p className="k-field-label">{f.label}</p>
                    <p className="k-field-value">{f.value || 'N/A'}</p>
                  </div>
                ))}
                <div className="k-info-block">
                  <p className="k-field-label">Invoice Amount</p>
                  <p className="k-field-value" style={{ color: 'var(--c-success)', fontWeight: 600 }}>
                    {taskInfo.invoiceAmount ? `₹${taskInfo.invoiceAmount}` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Task Details */}
            <div>
              <p className="k-section-title">Task Details</p>
              <div className="flex flex-col gap-2.5">
                {[
                  { label: 'Assigned To', value: getDoer(task) },
                  { label: 'Doer Email', value: getDoerEmail(task) },
                  { label: 'Planned Date', value: getPlannedDate(task) },
                  { label: 'Week', value: getWeek(task) },
                  { label: 'PC Deo', value: getPCDeo(task) },
                ].map((f, i) => (
                  <div key={i} className="k-info-block">
                    <p className="k-field-label">{f.label}</p>
                    <p className="k-field-value">{f.value || 'N/A'}</p>
                  </div>
                ))}
                <div className="k-info-block">
                  <p className="k-field-label">Delay</p>
                  <p className="k-field-value" style={{ color: getDelay(task) && parseFloat(getDelay(task)) > 0 ? 'var(--c-danger)' : 'var(--c-success)', fontWeight: 500 }}>
                    {getDelay(task) && parseFloat(getDelay(task)) > 0 ? `${getDelay(task)} days` : 'No delay'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Task Info */}
          {getCleanTaskInfo(task) && (
            <div>
              <p className="k-section-title">Task Information</p>
              <div className="k-remarks">{getCleanTaskInfo(task)}</div>
            </div>
          )}

          {/* What to Do */}
          <div>
            <p className="k-section-title">What to Do</p>
            <div className="k-remarks" style={{ whiteSpace: 'pre-wrap' }}>
              {getWhatToDo(task) || 'No instructions provided'}
            </div>
          </div>

          {/* External Link */}
          {getLink(task) && (
            <div>
              <p className="k-section-title">Related Link</p>
              <a href={getLink(task)} target="_blank" rel="noopener noreferrer" className="k-btn-gold">
                <ExternalLink size={14} /> Open External Link
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return createPortal(modalContent, document.body);
};

const FMSTasks = ({ currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Search / filter card stays pinned while the list scrolls under it.
  const [filterSentinelRef, isFilterStuck] = useStickyFilter();
  const [fmsFilter, setFmsFilter] = useState('all');
  const [doerFilter, setDoerFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [expandedFMS, setExpandedFMS] = useState({});

  // Fetch function for FMS data
  const fetchFMSData = useCallback(async () => {
    try {
      const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_FMS;
      const sheetName = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_FMS;
      
      const apiUrl = '/api/sheets';
      const params = new URLSearchParams({
        sheetId: spreadsheetId,
        sheetName: sheetName,
        range: 'A1:M'
      });
      
      const response = await fetch(`${apiUrl}?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const rows = data.values || [];

      if (rows.length === 0) {
        return [];
      }

      // Get header row
      const headers = rows[0];
      
      // Process data rows (skip header) and filter by current user
      const fmsTasks = rows.slice(1)
        .filter(row => row && row.length > 0) // Filter out empty rows
        .map((row, index) => {
          const task = {};
          
          // Map each column to its header
          headers.forEach((header, colIndex) => {
            if (header && row[colIndex] !== undefined) {
              const key = cleanHeaderName(header);
              task[key] = row[colIndex];
            }
          });

          // Add computed fields
          task.id = index + 1;
          task.rowNumber = index + 2; // Actual row number in sheet (1 for header + index)
          
          return task;
        })
        .filter(task => {
          // Filter tasks by current user's name in Doer column
          const taskDoer = getDoer(task);
          return taskDoer === currentUser.name;
        });

      return fmsTasks;
    } catch (error) {
      console.error('Error fetching FMS data:', error);
      throw error;
    }
  }, [currentUser.name]);

  
  // Use cached data hook
  const { data: allFMSTasks, loading, error, lastRefresh } = useCachedData(
    'fms', 
    currentUser, 
    fetchFMSData
  );

  
// Filter out CheckList Task and HelpTicket items (using direct property access)
const tasks = useMemo(() => {
  if (!Array.isArray(allFMSTasks)) return [];
  
  return allFMSTasks.filter(task => {
    const fmsType = task.fms || 'Unknown FMS';
    return !fmsType.toLowerCase().includes('checklist task') && 
           !fmsType.toLowerCase().includes('helpticket');
  });
}, [allFMSTasks]);

// Add local loading state for manual refresh
const [isManualRefreshing, setIsManualRefreshing] = useState(false);

// Enhanced refresh function that ensures fresh data from server  
const refresh = useCallback(async () => {
  console.log(`🔄 Force refreshing FMS data for ${currentUser.name} - fetching fresh from server...`);
  
  setIsManualRefreshing(true);
  
  try {
    // Import dataManager to clear cache first
    const { default: dataManager } = await import('../utils/DataManager');
    
    // Clear the specific cache entry to force fresh fetch
    const cacheKey = `fms_${currentUser.name}`;
    dataManager.cache.delete(cacheKey);
    console.log(`🗑️ Cleared cache for ${cacheKey}`);
    
    // Directly call the fetch function to get fresh data from server
    const freshData = await fetchFMSData();
    
    // Update cache with fresh data
    dataManager.setData('fms', currentUser.name, freshData);
    
    console.log(`✅ Successfully refreshed FMS data: ${freshData.length} tasks`);
    return freshData;
  } catch (error) {
    console.error('❌ Error during manual refresh:', error);
    throw error;
  } finally {
    setIsManualRefreshing(false);
  }
}, [fetchFMSData, currentUser.name]);

// Combined loading state
const isRefreshing = loading || isManualRefreshing;

  const cleanHeaderName = (header) => {
    return header
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };


  // Helper functions to extract data from task info
  const parseTaskInfo = (taskInfo) => {
    if (!taskInfo) return {};
    
    const info = {};
    const lines = taskInfo.split('<br>');
    
    lines.forEach(line => {
      if (line.includes('Oder ID') || line.includes('Order ID')) {
        info.orderId = line.split(' - ')[1] || '';
      } else if (line.includes('Name of the Client')) {
        info.clientName = line.split(' - ')[1] || '';
      } else if (line.includes('Mobile')) {
        info.mobile = line.split(' - ')[1] || '';
      } else if (line.includes('Invoice Amount')) {
        info.invoiceAmount = line.split(' - ')[1] || '';
      } else if (line.includes('Invoice No')) {
        info.invoiceNo = line.split(' - ')[1] || '';
      } else if (line.includes('Order Taken By')) {
        info.orderTakenBy = line.split(' - ')[1] || '';
      }
    });
    
    return info;
  };

  const getFMSType = (task) => {
    return task.fms || 'Unknown FMS';
  };

  const getTaskInfo = (task) => {
    return task.task_info || '';
  };

  const getWhatToDo = (task) => {
    return task.what_to_do || '';
  };

  const getDoer = (task) => {
    return task.doer || '';
  };

  const getPlannedDate = (task) => {
    return task.planned || '';
  };

  const getDelay = (task) => {
    return task.delay || '';
  };

  const getWeek = (task) => {
    return task.week || '';
  };

  const getLink = (task) => {
    return task.link || '';
  };

  // Group tasks by FMS
  const groupedTasks = tasks.reduce((groups, task) => {
    const fmsType = getFMSType(task);
    if (!groups[fmsType]) {
      groups[fmsType] = [];
    }
    groups[fmsType].push(task);
    return groups;
  }, {});

  // Get unique FMS types and doers for filters
  const fmsTypes = ['all', ...new Set(tasks.map(task => getFMSType(task)))];
  const doers = ['all', ...new Set(tasks.map(task => getDoer(task)).filter(doer => doer))];

  const filteredGroupedTasks = Object.keys(groupedTasks).reduce((filtered, fmsType) => {
    const fmsTasks = groupedTasks[fmsType].filter(task => {
      const matchesSearch = searchTerm === '' || 
        getTaskInfo(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getWhatToDo(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getDoer(task).toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFMS = fmsFilter === 'all' || getFMSType(task) === fmsFilter;
      const matchesDoer = doerFilter === 'all' || getDoer(task) === doerFilter;
      
      return matchesSearch && matchesFMS && matchesDoer;
    });

    if (fmsTasks.length > 0) {
      filtered[fmsType] = fmsTasks;
    }
    
    return filtered;
  }, {});

  const getTotalStats = () => {
    const total = tasks.length;
    const fmsTypeCount = Object.keys(groupedTasks).length;
    const uniqueDoers = new Set(tasks.map(task => getDoer(task)).filter(doer => doer)).size;
    
    return { total, fmsTypeCount, uniqueDoers };
  };

  const stats = getTotalStats();

  const toggleFMSExpansion = (fmsType) => {
    setExpandedFMS(prev => ({
      ...prev,
      [fmsType]: !prev[fmsType]
    }));
  };

  if (loading) {
    return (
      <div className="k-card">
        <div className="k-loading">
          <div className="k-loading-spinner"></div>
          <p className="k-loading-text">Loading FMS tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k-card">
        <div className="k-error">
          <div className="k-error-icon"><AlertTriangle className="w-7 h-7" /></div>
          <p className="k-error-title">Error Loading FMS Tasks</p>
          <p className="k-error-msg">{error}</p>
          <button onClick={refresh} className="k-btn-primary mt-2">
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="k-page">

      {/* Stats */}
      <div className="k-stats-grid">
        {[
          { label: 'Total Tasks',    value: stats.total,        icon: FileText, color: 'var(--teal-primary)', bg: 'var(--teal-dim)' },
          { label: 'FMS Types',      value: stats.fmsTypeCount, icon: FileText, color: 'var(--gold-primary)',  bg: 'var(--gold-dim)' },
          { label: 'Assigned Doers', value: stats.uniqueDoers,  icon: User,     color: 'var(--c-success)',     bg: 'var(--c-success-bg)' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="k-stat-card">
            <div className="k-stat-icon" style={{ background: bg }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div>
              <p className="k-stat-label">{label}</p>
              <p className="k-stat-value" style={{ color }}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div ref={filterSentinelRef} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />
      <div className={`k-card p-4 k-filter-sticky ${isFilterStuck ? 'is-stuck' : ''}`}>
        <div className="k-filter-bar">
          <div className="k-search-wrap">
            <Search size={14} className="k-search-icon" />
            <input
              type="text"
              placeholder="Search tasks, doers, or FMS types…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="k-search"
            />
          </div>
          <select value={fmsFilter} onChange={(e) => setFmsFilter(e.target.value)} className="k-select">
            {fmsTypes.map(f => <option key={f} value={f}>{f === 'all' ? 'All FMS Types' : f}</option>)}
          </select>
          <select value={doerFilter} onChange={(e) => setDoerFilter(e.target.value)} className="k-select">
            {doers.map(d => <option key={d} value={d}>{d === 'all' ? 'All Doers' : d}</option>)}
          </select>
          <button
            onClick={refresh}
            disabled={isRefreshing}
            title={lastRefresh ? `Refresh · Last updated ${new Date(lastRefresh).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Refresh data'}
            className="k-refresh-btn w-8 h-8 flex-shrink-0 px-0 justify-center"
            style={{ cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
          >
            <RefreshCw size={13} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Grouped Task List */}
      <div className="space-y-2.5">
        {Object.keys(filteredGroupedTasks).length === 0 ? (
          <div className="k-card">
            <div className="k-empty">
              <div className="k-empty-icon"><FileText className="w-7 h-7" /></div>
              <p className="k-empty-title">No FMS Tasks Found</p>
              <p className="k-empty-msg">
                {tasks.length === 0 ? `No FMS tasks assigned to ${currentUser.name}` : 'No tasks match your current filters'}
              </p>
            </div>
          </div>
        ) : (
          Object.keys(filteredGroupedTasks).map(fmsType => {
            const groupTasks = filteredGroupedTasks[fmsType];
            const overdueCount = groupTasks.filter(t => getDelay(t) && parseFloat(getDelay(t)) > 0).length;
            const isOpen = expandedFMS[fmsType];
            return (
              <div key={fmsType} className="k-card overflow-hidden">
                {/* Group Header */}
                <div
                  onClick={() => toggleFMSExpansion(fmsType)}
                  className="flex items-center justify-between gap-3 px-4 py-3.5 cursor-pointer"
                  style={{ borderBottom: isOpen ? '1px solid var(--border)' : 'none' }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="k-stat-icon w-9 h-9 rounded-xl flex-shrink-0"
                      style={{ background: overdueCount > 0 ? 'var(--c-danger-bg)' : 'var(--green-dim)' }}>
                      <FileText size={16} style={{ color: overdueCount > 0 ? 'var(--c-danger)' : 'var(--green-primary)' }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{fmsType}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {groupTasks.length} task{groupTasks.length !== 1 ? 's' : ''}
                        {overdueCount > 0 && ` · ${overdueCount} overdue`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {overdueCount > 0 && <span className="k-badge-overdue">{overdueCount} overdue</span>}
                    <span className="k-badge-score">{groupTasks.length}</span>
                    {isOpen ? <ChevronUp size={15} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={15} style={{ color: 'var(--text-muted)' }} />}
                  </div>
                </div>

                {/* Task rows */}
                {isOpen && (
                  <div className="k-task-list">
                    {groupTasks.map((task, idx) => {
                      const tInfo = parseTaskInfo(getTaskInfo(task));
                      const isDelayed = getDelay(task) && parseFloat(getDelay(task)) > 0;
                      return (
                        <div
                          key={idx}
                          className={`k-task-row ${isDelayed ? 'overdue' : 'low'}`}
                          onClick={() => { setSelectedTask(task); setShowModal(true); }}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5 mb-1">
                              {tInfo.clientName && <span className="k-badge-delegated">{tInfo.clientName}</span>}
                              {isDelayed && <span className="k-badge-overdue">Delayed</span>}
                              <span className="k-task-title flex-1 min-w-0">
                                {tInfo.orderId || getWhatToDo(task) || `Task ${task.id}`}
                              </span>
                            </div>
                            {getWhatToDo(task) && tInfo.orderId && (
                              <p className="text-xs line-clamp-1 mb-1" style={{ color: 'var(--text-secondary)' }}>
                                {getWhatToDo(task)}
                              </p>
                            )}
                            <div className="k-task-meta">
                              <span className="k-task-meta-item"><User size={11} /> {getDoer(task)}</span>
                              {tInfo.invoiceAmount && <span className="k-task-meta-item">₹{tInfo.invoiceAmount}</span>}
                              {getPlannedDate(task) && <span className="k-task-meta-item"><Calendar size={11} /> {getPlannedDate(task)}</span>}
                              {getWeek(task) && <span className="k-task-meta-item"><Clock size={11} /> {getWeek(task)}</span>}
                              {isDelayed && <span className="k-task-meta-item" style={{ color: 'var(--c-danger)' }}>
                                <AlertTriangle size={11} /> {getDelay(task)}d overdue
                              </span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 self-center">
                            {getLink(task) && (
                              <a
                                href={getLink(task)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title="Open external link"
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, background: 'var(--gold-dim)', color: 'var(--gold-primary)', border: '1px solid rgba(200,146,42,0.3)' }}
                              >
                                <ExternalLink size={13} />
                              </a>
                            )}
                            <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Portal Modal */}
      {showModal && selectedTask && (
        <TaskModal task={selectedTask} onClose={() => { setShowModal(false); setSelectedTask(null); }} />
      )}
    </div>
  );
};

export default FMSTasks;
